import React from 'react';
import { cn } from '../../utils/cn';

/**
 * PageHeader — entête de page signature Bold Duotone.
 *
 * `title` accepte une string ou un ReactNode pour permettre l'italique serif inline :
 *   <PageHeader title={<>Écrire, <span className="font-serif italic font-normal">recommencer.</span></>} />
 *
 * Variants size :
 *   - default : titre 2xl/3xl
 *   - hero    : titre énorme (40px/58px), leading-[0.95]
 *   - compact : plus petit (xl/2xl)
 *
 * bordered (default true) : ajoute un séparateur en bas.
 */

const SIZES = {
  default: 'text-2xl md:text-3xl font-bold tracking-tight leading-tight',
  hero:    'text-[40px] md:text-[58px] font-bold tracking-tight leading-[0.95]',
  compact: 'text-xl md:text-2xl font-bold tracking-tight leading-tight',
};

export function PageHeader({
  eyebrow,
  title,
  description,
  breadcrumb,
  actions,
  badge,
  size = 'default',
  bordered = true,
  className,
  ...rest
}) {
  return (
    <header
      className={cn(
        bordered && 'pb-6 border-b border-ink/12',
        className,
      )}
      {...rest}
    >
      {breadcrumb && <div className="mb-3">{breadcrumb}</div>}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-6">
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-mute mb-3">
              {eyebrow}
            </div>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className={cn(SIZES[size], 'text-text')}>{title}</h1>
            {badge && <span className="shrink-0">{badge}</span>}
          </div>
          {description && (
            <p className={cn(
              'text-text-2/85 leading-relaxed max-w-2xl',
              size === 'hero' ? 'text-[15.5px] mt-5' : 'text-sm md:text-base mt-2',
            )}>
              {description}
            </p>
          )}
        </div>
        {actions && <div className="shrink-0 flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

export default PageHeader;
